import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import EquationInput from '../../../../components/inputs/EquationInput';

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const normalize = (txt) =>
  String(txt || '')
    .replace(/\s/g, '')
    .toLowerCase()
    .replace(/\u00b7/g, '*')
    .replace(/\u2212/g, '-')
    .replace(/\u2013/g, '-');

function stripLeadingEq(raw, allowedLeftPrefixes) {
  const s = String(raw ?? '').trim();
  if (!s) return '';

  const compact = s.replace(/\s/g, '');
  const eqIdx = compact.indexOf('=');
  if (eqIdx === -1) return s;

  const left = compact.slice(0, eqIdx).toLowerCase();
  const right = compact.slice(eqIdx + 1);
  const ok = allowedLeftPrefixes.some((p) => left === p);
  return ok ? right : s;
}

function stripForN(raw) {
  return stripLeadingEq(raw, ['n']);
}

function stripRhsIfAny(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const compact = s.replace(/\s/g, '');
  const eqIdx = compact.indexOf('=');
  if (eqIdx === -1) return s;
  return compact.slice(eqIdx + 1);
}

function detectFirstVarLetter(raw) {
  const m = String(raw ?? '').match(/[a-z]/i);
  return m ? m[0].toLowerCase() : null;
}

function parseLinearRemainderForm(rhsRaw, modulus, expectedRest) {
  const s0 = String(rhsRaw ?? '').trim();
  if (!s0) return null;

  const s = normalize(s0).replace(/\(([a-z])\)/g, '$1');
  const m = s.match(/^(\d+)\*?([a-z])([+\-]\d+)$/i);
  if (!m) return null;

  const coeff = parseInt(m[1], 10);
  const variable = String(m[2]).toLowerCase();
  const constTerm = parseInt(m[3], 10);

  if (!Number.isFinite(coeff) || !Number.isFinite(constTerm)) return null;
  if (coeff !== modulus) return null;

  const rest = ((constTerm % modulus) + modulus) % modulus;
  if (rest !== expectedRest) return null;

  return { variable, constTerm };
}

function mapVar(exprRaw, fromVar, toVar = 'x') {
  const from = String(fromVar || '').toLowerCase();
  if (!from || from === toVar) return String(exprRaw ?? '');
  const re = new RegExp(`(?<![a-z])${from}(?![a-z])`, 'gi');
  return String(exprRaw ?? '').replace(re, toVar);
}

function findUnexpectedVars(rawExpr, allowedVars = []) {
  const s = normalize(rawExpr || '');
  if (!s) return [];
  const all = Array.from(new Set((s.match(/[a-z]/g) || []).map((v) => v.toLowerCase())));
  const allowed = new Set(allowedVars.map((v) => String(v || '').toLowerCase()).filter(Boolean));
  return all.filter((v) => !allowed.has(v));
}

function insertImplicitMultiplication(src) {
  let out = '';
  const s = String(src || '');
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    const prev = out[out.length - 1] || '';
    const prevAtom = /[0-9a-z)]/i.test(prev);
    const nextAtom = ch === '(' || /[0-9a-z]/i.test(ch);
    if (i > 0 && prevAtom && nextAtom) {
      const bothDigits = /[0-9]/.test(prev) && /[0-9]/.test(ch);
      const bothLetters = /[a-z]/i.test(prev) && /[a-z]/i.test(ch);
      if (!bothDigits && !bothLetters && ch !== '^') out += '*';
    }
    out += ch;
  }
  return out;
}

function toJsExpr(rawExpr) {
  const normalized = normalize(rawExpr)
    .replace(/\^\{([^}]+)\}/g, '^$1')
    .replace(/,/g, '.');

  if (!/^[0-9a-z+\-*/^().]*$/i.test(normalized)) return null;

  const withMul = insertImplicitMultiplication(normalized);
  return withMul.replace(/\^/g, '**');
}

function evalForX(rawExpr, x) {
  const jsExpr = toJsExpr(rawExpr);
  if (!jsExpr) return null;
  try {
    const value = Function('x', `return (${jsExpr});`)(x); // eslint-disable-line no-new-func
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function equivalentBySampling(rawExpr, expectedFn) {
  const samples = [-5, -2, -1, 0, 1, 2, 4, 7];
  for (const x of samples) {
    const got = evalForX(rawExpr, x);
    const exp = expectedFn(x);
    if (got === null || exp === null || Math.abs(got - exp) > 1e-9) return false;
  }
  return true;
}

function hasExplicitFactor(rawExpr, divisor) {
  const s = normalize(rawExpr);
  if (!s) return false;
  return s.includes(`${divisor}(`) || s.includes(`${divisor}*(`);
}

function hasUnexpandedSquare(rawExpr) {
  const s = normalize(rawExpr);
  if (!s) return false;
  return /\)\^(\{)?2(\})?/.test(s);
}

function toUserLatex(str) {
  let s = String(str ?? '');
  s = s.replace(/(?<!\\dfrac\{)(-?\d+)\/(\d+)/g, (_, n, d) => `\\dfrac{${n}}{${d}}`);
  s = s.replace(/\^(-?\d{2,})/g, '^{$1}');
  s = s.replace(/\*/g, '\\cdot ');
  return s;
}

function polyLatexFromABC(A, B, C) {
  const parts = [];
  if (A !== 0) {
    if (A === 1) parts.push('n^2');
    else if (A === -1) parts.push('-n^2');
    else parts.push(`${A}n^2`);
  }
  if (B !== 0) {
    if (parts.length === 0) {
      if (B === 1) parts.push('n');
      else if (B === -1) parts.push('-n');
      else parts.push(`${B}n`);
    } else if (B > 0) {
      if (B === 1) parts.push('+n');
      else parts.push(`+${B}n`);
    } else if (B === -1) {
      parts.push('-n');
    } else {
      parts.push(`${B}n`);
    }
  }
  if (C !== 0 || parts.length === 0) {
    if (parts.length === 0) parts.push(`${C}`);
    else if (C > 0) parts.push(`+${C}`);
    else parts.push(`${C}`);
  }
  return parts.join('');
}

function fmtPolyX(a2, a1, a0) {
  const parts = [];
  if (a2 !== 0) {
    if (a2 === 1) parts.push('x^2');
    else if (a2 === -1) parts.push('-x^2');
    else parts.push(`${a2}x^2`);
  }
  if (a1 !== 0) {
    const term = a1 === 1 ? 'x' : a1 === -1 ? '-x' : `${a1}x`;
    if (parts.length === 0) parts.push(term);
    else if (a1 > 0) parts.push(`+${term}`);
    else parts.push(term);
  }
  if (a0 !== 0 || parts.length === 0) {
    if (parts.length === 0) parts.push(`${a0}`);
    else if (a0 > 0) parts.push(`+${a0}`);
    else parts.push(`${a0}`);
  }
  return parts.join('');
}

function fmtPolyInN(coeffA, coeffB, coeffC) {
  const parts = [];
  if (coeffA !== 0) {
    if (coeffA === 1) parts.push('n^2');
    else if (coeffA === -1) parts.push('-n^2');
    else parts.push(`${coeffA}n^2`);
  }
  if (coeffB !== 0) {
    const term = coeffB === 1 ? 'n' : coeffB === -1 ? '-n' : `${coeffB}n`;
    if (parts.length === 0) parts.push(term);
    else if (coeffB > 0) parts.push(`+${term}`);
    else parts.push(term);
  }
  if (coeffC !== 0 || parts.length === 0) {
    if (parts.length === 0) parts.push(`${coeffC}`);
    else if (coeffC > 0) parts.push(`+${coeffC}`);
    else parts.push(`${coeffC}`);
  }
  return parts.join('');
}

function mod(a, m) {
  return ((a % m) + m) % m;
}

// ============================================================================
// GENERATOR
// ============================================================================

function generateMay2025OriginalTask() {
  return {
    type: 'MaturaRemainderDivisibilityOpenTask',
    source: {
      variant: 'original',
      exam: 'MATURA',
      session: '2025_MAJ',
      taskNumber: 5,
    },
    data: {
      modulus: 2,
      rest: 1,
      divisor: 4,
      coeffA: 3,
      coeffB: 2,
      coeffC: 7,
      exprLatex: '3n^2+2n+7',
      expandedX: { a2: 12, a1: 16, a0: 12 },
      insideX: { a2: 3, a1: 4, a0: 3 },
      maxPoints: 2,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === 'original' && options.session === '2025_MAJ') {
    return generateMay2025OriginalTask();
  }

  const scenarios = [
    { modulus: 2, rests: [1], divisors: [4] },
    { modulus: 3, rests: [1, 2], divisors: [3, 9] },
    { modulus: 4, rests: [1, 2, 3], divisors: [4] },
    { modulus: 5, rests: [1, 2, 3, 4], divisors: [5, 25] },
  ];
  const scenario = pick(scenarios);
  const { modulus } = scenario;
  const rest = pick(scenario.rests);
  const divisor = pick(scenario.divisors);

  // Dwa typy konstrukcji:
  // T1: (n-r)*(pn+q) + d*t   => podzielne przez d po podstawieniu n=mx+r
  // T2: p*(n-r)^2 + d*t      => też po podstawieniu podzielne przez d
  let coeffA = 0;
  let coeffB = 0;
  let coeffC = 0;
  let expandedX = null;
  let insideX = null;

  const isDivBy = (n, A, B, C, d) => mod(A * n * n + B * n + C, d) === 0;

  for (let attempts = 0; attempts < 400; attempts += 1) {
    const type = pick(['T1', 'T2']);

    if (type === 'T1') {
      const p = pick([1, 2, 3]);
      const q = pick([-6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6]);
      const t = pick([-6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6]);

      // (n-r)(pn+q) + d*t
      const A = p;
      const B = q - p * rest;
      const C = divisor * t - q * rest;

      // Po podstawieniu n = m x + r:
      // A(mx+r)^2 + B(mx+r) + C = a2 x^2 + a1 x + a0
      // gdzie A=p, B=q-pr, C=d*t-qr.
      const a2 = A * modulus * modulus;
      const a1 = 2 * A * modulus * rest + B * modulus;
      const a0 = A * rest * rest + B * rest + C;

      if (a2 % divisor !== 0 || a1 % divisor !== 0 || a0 % divisor !== 0) continue;

      const in2 = a2 / divisor;
      const in1 = a1 / divisor;
      const in0 = a0 / divisor;

      if ([A, B, C].every((v) => v % divisor === 0)) continue; // trywialne globalnie
      if (B === 0 || C === 0) continue; // unikamy banalnych postaci w treści
      if (in1 === 0 || in0 === 0) continue; // unikamy 0x i +0 po wyłączeniu

      // weryfikacja: dla klasy rest ma działać, poza nią nie może działać "zawsze"
      let okOnRequiredClass = true;
      let hasCounterExampleOutside = false;
      for (let n = 0; n <= 60; n += 1) {
        const inRequiredClass = mod(n, modulus) === rest;
        const divisible = isDivBy(n, A, B, C, divisor);
        if (inRequiredClass && !divisible) okOnRequiredClass = false;
        if (!inRequiredClass && !divisible) hasCounterExampleOutside = true;
      }
      if (!okOnRequiredClass || !hasCounterExampleOutside) continue;

      if (divisor === modulus) continue; // unikamy zbyt oczywistego "ta sama liczba"

      coeffA = A;
      coeffB = B;
      coeffC = C;
      expandedX = { a2, a1, a0 };
      insideX = { a2: in2, a1: in1, a0: in0 };
      break;
    }

    // T2
    const p = pick([1, 2, 3]);
    const t = pick([-6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6]);

    // p(n-r)^2 + d*t
    const A = p;
    const B = -2 * p * rest;
    const C = p * rest * rest + divisor * t;

    // Po podstawieniu n = m x + r:
    // A(mx+r)^2 + B(mx+r) + C = a2 x^2 + a1 x + a0
    const a2 = A * modulus * modulus;
    const a1 = 2 * A * modulus * rest + B * modulus;
    const a0 = A * rest * rest + B * rest + C;

    if (a2 % divisor !== 0 || a1 % divisor !== 0 || a0 % divisor !== 0) continue;

    const in2 = a2 / divisor;
    const in1 = a1 / divisor;
    const in0 = a0 / divisor;

    if ([A, B, C].every((v) => v % divisor === 0)) continue; // trywialne globalnie
    if (B === 0 || C === 0) continue; // unikamy banalnych postaci w treści
    if (in1 === 0 || in0 === 0) continue; // unikamy 0x i +0 po wyłączeniu

    let okOnRequiredClass = true;
    let hasCounterExampleOutside = false;
    for (let n = 0; n <= 60; n += 1) {
      const inRequiredClass = mod(n, modulus) === rest;
      const divisible = isDivBy(n, A, B, C, divisor);
      if (inRequiredClass && !divisible) okOnRequiredClass = false;
      if (!inRequiredClass && !divisible) hasCounterExampleOutside = true;
    }
    if (!okOnRequiredClass || !hasCounterExampleOutside) continue;

    if (divisor === modulus) continue; // unikamy zbyt oczywistego "ta sama liczba"

    coeffA = A;
    coeffB = B;
    coeffC = C;
    expandedX = { a2, a1, a0 };
    insideX = { a2: in2, a1: in1, a0: in0 };
    break;
  }

  // Fallback awaryjny (spójny z aktualnym modulus/rest/divisor)
  if (!expandedX || !insideX) {
    const s = pick([1, 2, 3, 4]);

    // Rodzina:
    // E(n) = n^2 + (d-2r)n + (r^2 + d*s)
    // Dla n = m x + r:
    // E = m^2 x^2 + m d x + d(r+s)
    // W scenariuszach tej lekcji d | m^2, więc da się wyłączyć d.
    coeffA = 1;
    coeffB = divisor - 2 * rest;
    coeffC = rest * rest + divisor * s;

    const a2 = modulus * modulus;
    const a1 = modulus * divisor;
    const a0 = divisor * (rest + s);

    expandedX = { a2, a1, a0 };

    if (a2 % divisor === 0) {
      insideX = { a2: a2 / divisor, a1: a1 / divisor, a0: a0 / divisor };
    } else {
      // Bezpiecznik dla ewentualnych przyszłych scenariuszy (gdyby d nie dzieliło m^2).
      expandedX = {
        a2: divisor * a2,
        a1: divisor * a1,
        a0: divisor * (rest + s),
      };
      insideX = { a2, a1, a0: rest + s };
    }
  }

  return {
    type: 'MaturaRemainderDivisibilityOpenTask',
    data: {
      modulus,
      rest,
      divisor,
      coeffA,
      coeffB,
      coeffC,
      exprLatex: polyLatexFromABC(coeffA, coeffB, coeffC),
      expandedX,
      insideX,
      maxPoints: 2,
    },
  };
}

// ============================================================================
// TASK
// ============================================================================

export function renderTask({ data }) {
  const { modulus, rest, exprLatex, divisor, maxPoints } = data;
  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Zadanie maturalne – otwarte (0–{maxPoints} pkt).</strong>
      </p>
      <p>
        Wykaż, że dla każdej liczby naturalnej <InlineMath math={'n'} />, która przy dzieleniu przez{' '}
        <InlineMath math={String(modulus)} /> daje resztę <InlineMath math={String(rest)} />, liczba{' '}
        <InlineMath math={exprLatex} /> jest podzielna przez <InlineMath math={String(divisor)} />.
      </p>
      <p>Uzupełnij trzy kluczowe kroki dowodu.</p>
    </div>
  );
}
// ============================================================================
// INPUT
// ============================================================================

export function renderInput(task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const { modulus, exprLatex, divisor } = task.data;

  const handleStep1 = (raw) => onChange?.({ ...value, step1: raw });
  const handleStep2 = (raw) => onChange?.({ ...value, step2: raw });
  const handleStep3 = (raw) => onChange?.({ ...value, step3: raw });

  const varHint = detectFirstVarLetter(value.step1);

  return (
    <div className="space-y-4" style={{ marginTop: 12, textAlign: 'left' }}>
      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <strong>Krok 1.</strong> Zapisz liczbę <InlineMath math={'n'} /> w postaci zależnej od{' '}
          <InlineMath math={String(modulus)} />.
        </div>
        <div style={{ marginBottom: 6, opacity: 0.9 }}>
          <InlineMath math={'n='} />
        </div>
        <EquationInput
          value={value.step1 || ''}
          onChange={handleStep1}
          disabled={disabled}
          placeholder={'np. 2x+1'}
          showPreview
        />
        {varHint && (
          <div style={{ marginTop: 6, opacity: 0.85, fontSize: 12 }}>
            Założenie: <InlineMath math={`${varHint} \\in \\mathbb{Z}`} />
          </div>
        )}
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <strong>Krok 2.</strong> Podstaw do <InlineMath math={exprLatex} />, rozwiń i uprość
          (bez wyłączania <InlineMath math={String(divisor)} />).
        </div>
        <EquationInput
          value={value.step2 || ''}
          onChange={handleStep2}
          disabled={disabled}
          placeholder={'np. 75x^2+20x+6'}
          showPreview
        />
      </div>

      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <strong>Krok 3.</strong> Wyłącz czynnik <InlineMath math={String(divisor)} /> i zapisz postać końcową.
        </div>
        <EquationInput
          value={value.step3 || ''}
          onChange={handleStep3}
          disabled={disabled}
          placeholder={`${divisor}(...)`}
          showPreview
        />
      </div>
    </div>
  );
}
// ============================================================================
// CHECK
// ============================================================================

export function checkAnswer({ data }, userAnswer) {
  const { modulus, rest, divisor, expandedX, maxPoints } = data;

  const empty = {
    isCorrect: false,
    score: 0,
    maxScore: maxPoints,
    parts: { step1: false, step2: false, step3: false },
  };

  if (!userAnswer || typeof userAnswer !== 'object') return empty;

  const step1Raw = String(userAnswer.step1 || '').trim();
  const step2Raw = String(userAnswer.step2 || '').trim();
  const step3Raw = String(userAnswer.step3 || '').trim();
  if (!step1Raw && !step2Raw && !step3Raw) return empty;

  const step1Rhs = stripForN(step1Raw);
  const step1Parsed = parseLinearRemainderForm(step1Rhs, modulus, rest);
  const step1Ok = !!step1Parsed;

  let step2Ok = false;
  let step3Ok = false;
  let extraVars2 = [];
  let extraVars3 = [];
  let step2HasUnexpandedSquare = false;

  if (step1Parsed) {
    const mainVar = step1Parsed.variable;
    const expectedExpanded = (x) => expandedX.a2 * x * x + expandedX.a1 * x + expandedX.a0;

    if (step2Raw) {
      const rhs2 = stripRhsIfAny(step2Raw);
      extraVars2 = findUnexpectedVars(rhs2, [mainVar]);
      const mapped2 = mapVar(rhs2, mainVar, 'x');
      const eq2 = equivalentBySampling(mapped2, expectedExpanded);
      step2HasUnexpandedSquare = hasUnexpandedSquare(mapped2);
      step2Ok = eq2 && !step2HasUnexpandedSquare && extraVars2.length === 0;
    }

    if (step3Raw) {
      const rhs3 = stripRhsIfAny(step3Raw);
      extraVars3 = findUnexpectedVars(rhs3, [mainVar]);
      const mapped3 = mapVar(rhs3, mainVar, 'x');
      const eq3 = equivalentBySampling(mapped3, expectedExpanded);
      const explicit3 = hasExplicitFactor(mapped3, divisor);
      step3Ok = eq3 && explicit3 && extraVars3.length === 0;
    }
  }

  // CKE-like:
  // 1 pkt: poprawny krok 1 + poprawny krok 2
  // 2 pkt: poprawny krok 1 + poprawny krok 3 (nawet gdy krok 2 jest błędny)
  let score = 0;
  if (step1Ok && step2Ok) score = 1;
  if (step1Ok && step3Ok) score = 2;

  const hints = [];
  if (step2Raw && step1Parsed && extraVars2.length > 0) {
    hints.push(
      `Uwaga: Krok 2: użyj konsekwentnie tych samych zmiennych co w kroku 1 (${step1Parsed.variable}). ` +
        `Wykryto dodatkowe oznaczenia: ${extraVars2.join(', ')}.`
    );
  }
  if (step2Raw && step1Parsed && step2HasUnexpandedSquare) {
    hints.push('Uwaga: Krok 2: rozwiń wzór skróconego mnożenia — nie zostawiaj postaci ( ... )^2.');
  }
  if (step3Raw && step1Parsed && extraVars3.length > 0) {
    hints.push(
      `Uwaga: Krok 3: użyj konsekwentnie tych samych zmiennych co w kroku 1 (${step1Parsed.variable}). ` +
        `Wykryto dodatkowe oznaczenia: ${extraVars3.join(', ')}.`
    );
  }

  return {
    isCorrect: score === maxPoints,
    score,
    maxScore: maxPoints,
    parts: { step1: step1Ok, step2: step2Ok, step3: step3Ok },
    ...(hints.length
      ? {
          messageType: 'warning',
          message: hints.join('\n'),
        }
      : {}),
  };
}
// ============================================================================
// ANSWER
// ============================================================================

export function renderAnswer({ data }) {
  const { modulus, rest, divisor, coeffA, coeffB, coeffC, expandedX, insideX } = data;

  const step1 = `n=${modulus}x+${rest},\\;x\\in\\mathbb{Z}`;
  const exprN = fmtPolyInN(coeffA, coeffB, coeffC);
  const step2 = `${exprN}=${fmtPolyX(expandedX.a2, expandedX.a1, expandedX.a0)}`;
  const step3 = `${exprN}=${divisor}\\left(${fmtPolyX(insideX.a2, insideX.a1, insideX.a0)}\\right)`;

  return (
    <div style={{ textAlign: 'left' }}>
      <p>
        <strong>Krok 1:</strong> <InlineMath math={step1} />
      </p>
      <p>
        <strong>Krok 2:</strong> <InlineMath math={step2} />
      </p>
      <p>
        <strong>Krok 3:</strong> <InlineMath math={step3} />
      </p>
    </div>
  );
}
// ============================================================================
// EXPLANATION
// ============================================================================

export function renderExplanation({ data }) {
  const { modulus, rest, divisor, coeffA, coeffB, coeffC, exprLatex, expandedX, insideX } = data;

  const step1 = `n=${modulus}x+${rest},\\;x\\in\\mathbb{Z}`;
  const exprN = fmtPolyInN(coeffA, coeffB, coeffC);

  const substituted = `${coeffA}(${modulus}x+${rest})^2${coeffB >= 0 ? '+' : ''}${coeffB}(${modulus}x+${rest})${coeffC >= 0 ? '+' : ''}${coeffC}`;
  const sqX2 = modulus * modulus;
  const sqX1 = 2 * modulus * rest;
  const sqX0 = rest * rest;
  const squareExpanded = `${coeffA}(${sqX2}x^2${sqX1 >= 0 ? '+' : ''}${sqX1}x${sqX0 >= 0 ? '+' : ''}${sqX0})${coeffB >= 0 ? '+' : ''}${coeffB}(${modulus}x+${rest})${coeffC >= 0 ? '+' : ''}${coeffC}`;

  const term1x2 = coeffA * sqX2;
  const term1x = coeffA * sqX1;
  const term1c = coeffA * sqX0;
  const term2x = coeffB * modulus;
  const term2c = coeffB * rest;
  const productsExpanded =
    `${term1x2}x^2` +
    `${term1x >= 0 ? '+' : ''}${term1x}x` +
    `${term1c >= 0 ? '+' : ''}${term1c}` +
    `${term2x >= 0 ? '+' : ''}${term2x}x` +
    `${term2c >= 0 ? '+' : ''}${term2c}` +
    `${coeffC >= 0 ? '+' : ''}${coeffC}`;

  return (
    <div className="space-y-3" style={{ textAlign: 'left', width: '100%', display: 'block' }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy z warunku o reszcie:
      </p>
      <BlockMath math={step1} />

      <p>
        <strong>Krok 2.</strong> Podstawiamy do <InlineMath math={exprLatex} />, rozwijamy wzór skróconego mnożenia,
        wykonujemy mnożenia i redukujemy wyrazy podobne:
      </p>
      <BlockMath math={exprN} />
      <BlockMath math={`=${substituted}`} />
      <BlockMath math={`=${squareExpanded}`} />
      <BlockMath math={`=${productsExpanded}`} />
      <BlockMath math={`=${fmtPolyX(expandedX.a2, expandedX.a1, expandedX.a0)}`} />

      <p>
        <strong>Krok 3.</strong> Wyłączamy czynnik <InlineMath math={String(divisor)} />:
      </p>
      <BlockMath math={exprN} />
      <BlockMath math={`=${divisor}\\left(${fmtPolyX(insideX.a2, insideX.a1, insideX.a0)}\\right)`} />

      <p>
        Ponieważ <InlineMath math={"x\\in\\mathbb{Z}"} />, wyrażenie w nawiasie jest całkowite.
        Otrzymaliśmy więc postać <InlineMath math={`${divisor}\\cdot(\\ldots)`} />, zatem wyrażenie jest podzielne przez{' '}
        <InlineMath math={String(divisor)} />.
      </p>
    </div>
  );
}

// ============================================================================
// USER ANSWER
// ============================================================================

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || (!userAnswer.step1 && !userAnswer.step2 && !userAnswer.step3)) {
    return <i>(brak odpowiedzi)</i>;
  }

  const rhs1 = userAnswer.step1 ? stripForN(userAnswer.step1) : '';
  const rhs2 = userAnswer.step2 ? stripRhsIfAny(userAnswer.step2) : '';
  const rhs3 = userAnswer.step3 ? stripRhsIfAny(userAnswer.step3) : '';
  const v = detectFirstVarLetter(rhs1) || 'x';

  return (
    <div style={{ whiteSpace: 'pre-wrap', marginTop: 8, textAlign: 'left' }}>
      {!!userAnswer.step1 && (
        <>
          <p>
            <InlineMath math={'n='} /> <InlineMath math={toUserLatex(rhs1)} />
          </p>
          <p style={{ marginTop: -6, opacity: 0.85, fontSize: 13 }}>
            <InlineMath math={`${v} \\in \\mathbb{Z}`} />
          </p>
        </>
      )}

      {!!userAnswer.step2 && (
        <p>
          <strong>Krok 2:</strong> <InlineMath math={toUserLatex(rhs2)} />
        </p>
      )}

      {!!userAnswer.step3 && (
        <p>
          <strong>Krok 3:</strong> <InlineMath math={toUserLatex(rhs3)} />
        </p>
      )}
    </div>
  );
}
